import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function DoubtsResolution({ user, onLogout }) {
  const [doubts, setDoubts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [answeringDoubt, setAnsweringDoubt] = useState(null);
  const [answer, setAnswer] = useState('');

  useEffect(() => {
    fetchDoubts();
  }, [filter]);

  const fetchDoubts = async () => {
    try {
      const url = filter === 'all' ? `${API}/doubts` : `${API}/doubts?status=${filter}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setDoubts(res.data);
    } catch (error) {
      console.error('Failed to fetch doubts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (doubtId) => {
    if (!answer.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    try {
      await axios.put(`${API}/doubts/${doubtId}/answer`, { answer }, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        params: { answer }
      });
      toast.success('Answer submitted successfully!');
      setAnsweringDoubt(null);
      setAnswer('');
      fetchDoubts();
    } catch (error) {
      console.error('Failed to answer doubt:', error);
      toast.error('Failed to submit answer');
    }
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="doubts-resolution"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="doubts-resolution">
      <div className="p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-navy">❓ Doubts Resolution</h1>
          <p className="text-[#4D4D4D] mt-2 font-light">Help students with their questions</p>
        </div>

        {/* Filter */}
        <Card className="border-0 card-shadow bg-white rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <label className="font-semibold text-navy">Filter:</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 h-10 border-2 border-[#E7E7E7] rounded-xl focus:outline-none focus:border-primary-blue transition-smooth"
              >
                <option value="open">Open Doubts</option>
                <option value="answered">Answered</option>
                <option value="all">All Doubts</option>
              </select>
              <div className="ml-auto text-sm text-[#4D4D4D]">
                {doubts.length} doubt{doubts.length !== 1 ? 's' : ''}
              </div>
            </div>
          </CardContent>
        </Card>

        {doubts.length === 0 ? (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
              <p className="text-xl font-bold text-navy">No doubts to display</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {doubts.map((doubt) => (
              <Card key={doubt.id} className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        doubt.status === 'answered' ? 'bg-green-50' : 'bg-blue-50'
                      }`}
                    >
                      {doubt.status === 'answered' ? (
                        <CheckCircle className="w-6 h-6 text-green-600" strokeWidth={2} />
                      ) : (
                        <AlertCircle className="w-6 h-6 text-primary-blue" strokeWidth={2} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-bold text-navy">{doubt.title}</h3>
                          <p className="text-sm text-[#A5A5A5]">by {doubt.student_name}</p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            doubt.status === 'answered' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-blue-100 text-primary-blue'
                          }`}
                        >
                          {doubt.status}
                        </span>
                      </div>
                      <p className="text-[#4D4D4D] mb-3">{doubt.description}</p>
                      
                      {doubt.status === 'answered' && doubt.answer && (
                        <div className="mt-4 p-4 rounded-xl bg-green-50">
                          <p className="text-sm font-semibold text-green-700 mb-2">✅ Answer:</p>
                          <p className="text-[#4D4D4D]">{doubt.answer}</p>
                          <p className="text-xs text-[#A5A5A5] mt-2">
                            Answered on {new Date(doubt.answered_at).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {doubt.status === 'open' && (
                        <div className="mt-4">
                          {answeringDoubt === doubt.id ? (
                            <div className="space-y-3">
                              <Textarea
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                placeholder="Type your answer here..."
                                rows={4}
                                className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth"
                              />
                              <div className="flex space-x-2">
                                <Button
                                  onClick={() => handleAnswer(doubt.id)}
                                  className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press"
                                >
                                  Submit Answer
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setAnsweringDoubt(null);
                                    setAnswer('');
                                  }}
                                  className="border-2 border-[#E7E7E7] rounded-xl transition-smooth"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              onClick={() => setAnsweringDoubt(doubt.id)}
                              className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press"
                            >
                              Answer This Doubt
                            </Button>
                          )}
                        </div>
                      )}

                      <p className="text-xs text-gray-500 mt-3">
                        Created: {new Date(doubt.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}